import { ApolloClient, FetchResult, MutationFunctionOptions, useApolloClient } from '@apollo/client'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Button, Paper, Stack } from '@mui/material'
import classNames from 'classnames'
import forEach from 'lodash/forEach'
import get from 'lodash/get'
import moment from 'moment'
import React, { ChangeEvent } from 'react'
import { Mutation } from 'react-apollo'
import { i18n } from '../index'
import { upsertVitrineProductTarget } from './productQueries'
import css from './productTable.css'
import type { VitrineProductTarget, VitrineProductTargetItem } from './productTypes'

type Props = {
  tabs: string[]
  activeTab: string
  items: VitrineProductTarget[]
  onTabClick: (...args: any[]) => any
  date: string
  stationId: string
  client: ApolloClient<object>
}

type OnMutateCallback = (options: MutationFunctionOptions<any, any, any>) => Promise<FetchResult<any, any, any>>

type DefaultValuesType = ReturnType<ProductTableComponent['getDefaultMutationVariables']>

type State = {
  items: VitrineProductTarget[]
}

type OnChangeEventType = (
  event: ChangeEvent<HTMLInputElement>,
  productId: string,
  activeTimeSlot: string,
  onMutate: OnMutateCallback,
  defaultVariables: DefaultValuesType,
) => void

const getProductData = (targets: VitrineProductTargetItem[], activeTimeSlot: string) => {
  const productData = {
    estimate: 0,
    target: 0,
  }
  forEach(targets, (item) => {
    if (item.timeslot === activeTimeSlot) {
      productData.estimate = item.estimate
      productData.target = item.target
    }
  })
  return productData
}

const getEditableProductField = (
  targets: VitrineProductTargetItem[],
  activeTimeSlot: string,
  productID: string,
  field: 'readyNow' | 'toBeMade',
  onChange: OnChangeEventType,
  defaultVariables: DefaultValuesType,
  editable: boolean,
) => {
  let value = 0
  let max: number | undefined
  forEach(targets, (item) => {
    if (item.timeslot === activeTimeSlot) {
      value = item[field]
      if (field === 'readyNow') {
        max = item.target
      }
    }
  })
  return (
    <Mutation
      mutation={upsertVitrineProductTarget}
      update={(cache, { data: { upsertVitrineProductTarget } }: any) => {
        cache.modify<{}>({
          fields: {
            vitrineProductTargets: (
              targets: ReadonlyArray<{
                targets: ReadonlyArray<VitrineProductTargetItem>
                product: { __ref: string }
                __typename: string
              }>,
            ) =>
              targets.map((target) => {
                const { product, targets: newTargets } = upsertVitrineProductTarget
                return target.product.__ref === `${product.__typename}:${product.id}`
                  ? { ...target, targets: newTargets }
                  : { ...target }
              }),
          },
        })
      }}
    >
      {(onMutate) =>
        editable ? (
          <input
            className={css.textField}
            value={value || ''}
            type='number'
            placeholder='0'
            max={max}
            min={0}
            onChange={(e) => {
              onChange(e, productID, activeTimeSlot, onMutate, defaultVariables)
            }}
          />
        ) : (
          <span>{value}</span>
        )
      }
    </Mutation>
  )
}

export class ProductTableComponent extends React.Component<Props, State> {
  state = {
    items: this.props.items,
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.date !== this.props.date || prevProps.activeTab !== this.props.activeTab) {
      this.setState({
        items: this.props.items,
      })
    }
  }

  onItemReadyNowChange = (
    e: ChangeEvent<HTMLInputElement>,
    productID: string,
    timeslot: string,
    callback: OnMutateCallback,
    defaultVariables: DefaultValuesType,
  ) => {
    const readyNowAmount = Number(e.target.value)
    const items = this.state.items.map((item) => {
      if (item.product.id === productID) {
        return {
          ...item,
          targets: item.targets.map((target) =>
            target.timeslot === timeslot ? { ...target, readyNow: readyNowAmount } : target,
          ),
        }
      }
      return item
    })
    const currentTarget = items
      .find((item) => item.product.id === productID)
      ?.targets.find((target) => target.timeslot === timeslot)
    callback({
      variables: { ...defaultVariables, readyNow: readyNowAmount, toBeMade: currentTarget?.toBeMade ?? 0 },
    })
    this.setState({
      items,
    })
  }

  onItemToBeMadeChange = (
    e: ChangeEvent<HTMLInputElement>,
    productID: string,
    timeslot: string,
    callback: OnMutateCallback,
    defaultVariables: DefaultValuesType,
  ) => {
    const toBeMadeAmount = Number(e.target.value)
    const items = this.state.items.map((item) => {
      if (item.product.id === productID) {
        return {
          ...item,
          targets: item.targets.map((target) =>
            target.timeslot === timeslot ? { ...target, toBeMade: toBeMadeAmount } : target,
          ),
        }
      }
      return item
    })
    const currentTarget = items
      .find((item) => item.product.id === productID)
      ?.targets.find((target) => target.timeslot === timeslot)
    callback({
      variables: { ...defaultVariables, readyNow: currentTarget?.readyNow ?? 0, toBeMade: toBeMadeAmount },
    })
    this.setState({
      items,
    })
  }

  getDefaultMutationVariables = (item: VitrineProductTarget, activeTab: string) => {
    const { stationId, date } = this.props
    return {
      stationId,
      date,
      vitrineProductId: item.product.id,
      timeslot: activeTab,
    }
  }

  // Copies the source day's estimate into today's toBeMade values (for the
  // tabs/timeslots this table shows), persisting each changed cell. readyNow
  // is left untouched.
  applyCopiedItems = async (sourceItems: VitrineProductTarget[]) => {
    const { tabs, stationId, date, client } = this.props
    const updates: Array<{ productId: string; timeslot: string; readyNow: number; toBeMade: number }> = []

    const newItems = this.state.items.map((item) => {
      const sourceItem = sourceItems.find((source) => source.product.id === item.product.id)
      if (!sourceItem) {
        return item
      }
      const newTargets = item.targets.map((target) => {
        if (!tabs.includes(target.timeslot)) {
          return target
        }
        const sourceTarget = sourceItem.targets.find((t) => t.timeslot === target.timeslot)
        if (!sourceTarget) {
          return target
        }
        updates.push({
          productId: item.product.id,
          timeslot: target.timeslot,
          readyNow: target.readyNow,
          toBeMade: sourceTarget.estimate,
        })
        return { ...target, toBeMade: sourceTarget.estimate }
      })
      return { ...item, targets: newTargets }
    })

    this.setState({ items: newItems })

    await Promise.all(
      updates.map((update) =>
        client.mutate({
          mutation: upsertVitrineProductTarget,
          variables: {
            stationId,
            date,
            vitrineProductId: update.productId,
            timeslot: update.timeslot,
            readyNow: update.readyNow,
            toBeMade: update.toBeMade,
          },
        }),
      ),
    )
  }

  static defaultProps = {
    onTabClick: () => {},
  }

  render() {
    const { tabs, activeTab, onTabClick, date } = this.props
    const { items } = this.state
    const editable = date === moment().format('YYYY-MM-DD')
    const sortedItems = [...items].sort((a, b) => {
      const targetA = a.targets.find((item) => item.timeslot === activeTab)
      const targetB = b.targets.find((item) => item.timeslot === activeTab)
      return (targetA?.estimate || 0) > (targetB?.estimate || 0) ? -1 : 1
    })
    return (
      <div className={css.component}>
        <Paper>
          <div className={css.tabs}>
            {tabs.map((tab, i) => {
              return (
                <a
                  key={i}
                  onClick={() => onTabClick(tab)}
                  className={classNames(css.tab, {
                    [css.tabActive]: tab === activeTab,
                  })}
                >
                  <ScheduleIcon /> {tab}
                </a>
              )
            })}
          </div>
          <div className={css.tableWrapper}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th className={css.nameHeadColumn}>{i18n.t('productTable.columnLabelName')}</th>
                  <th>{i18n.t('productTable.columnLabelTodaysEstimate')}</th>
                  <th>{i18n.t('productTable.columnLabelReadyTarget')}</th>
                  <th>{i18n.t('productTable.columnLabelReadyNow')}</th>
                  <th>{i18n.t('productTable.columnLabelNeedsToBePrepared')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item, productIndex) => {
                  const productData = getProductData(get(item, 'targets', []), activeTab)
                  const productID = item.product.id
                  return (
                    <tr key={productIndex}>
                      <th className={css.nameColumn}>{get(item, 'product.name', '')}</th>
                      <td className={css.readyTargetColumn}>{productData.estimate}</td>
                      <td className={css.readyTargetColumn}>{productData.target}</td>
                      <td className={css.readyTargetColumn}>
                        {getEditableProductField(
                          get(item, 'targets', []),
                          activeTab,
                          productID,
                          'readyNow',
                          this.onItemReadyNowChange,
                          this.getDefaultMutationVariables(item, activeTab),
                          editable,
                        )}
                      </td>
                      <td className={css.readyTargetColumn}>
                        {getEditableProductField(
                          get(item, 'targets', []),
                          activeTab,
                          productID,
                          'toBeMade',
                          this.onItemToBeMadeChange,
                          this.getDefaultMutationVariables(item, activeTab),
                          editable,
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Paper>
      </div>
    )
  }
}

type ExternalProps = Omit<Props, 'client'>

// Wraps the class component to inject the Apollo client so `applyCopiedItems`
// can issue mutations imperatively (bulk-copying values across timeslots/tabs).
const ProductTable = React.forwardRef<ProductTableComponent, ExternalProps>((props, ref) => {
  const client = useApolloClient()
  return <ProductTableComponent {...props} client={client} ref={ref} />
})

export default ProductTable
